import React, { useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import {
  Trash2,
  Clock,
  Maximize2,
  Layout,
  ArrowLeftRight,
  Zap,
  Eye,
  EyeOff,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import LayoutSelector from "./LayoutSelector";
import ImageUpload from "./ImageUpload";
import PositionSelector from "./PositionSelector";
import TextEditor from "./TextEditor";
import VideoUrlInput from "./VideoUrlInput";
import TeletekstInput from "./TeletekstInput";
import IframeUrlInput from "./IframeUrlInput";
import GalleryInput from "./GalleryInput";
import CountdownInput from "./CountdownInput";
import AgendaInput from "./AgendaInput";
import EmailInput from "./EmailInput";
import SportlinkInput from "./SportlinkInput";
import WeatherInput from "./WeatherInput";
import QrFeedInput from "./QrFeedInput";
import BackgroundColorInput from "./BackgroundColorInput";
import SlideEffectsModal from "../SlideEffectsModal";
import { isSlideCurrentlyInWindow } from "../../../utils/timeRestriction";
import { isCountdownExpired } from "../../../utils/countdownUtils";

function EditModal({
  slide,
  slideTypes = {},
  modalImageUrl,
  modalTinyMCEContent,
  imagePosition,
  slideLayout,
  uploadingImage,
  slideName,
  slideDuration,
  showBar,
  videoUrl,
  videoSound,
  imageSide,
  slideBgColor,
  onSlideBgColorChange,
  defaultSlideBgColor = "#FFFFFF",
  slideTransition,
  enabledFonts,
  typography,
  teletekstChannel,
  teletekstTheme,
  teletekstPages,
  teletekstSkipTopLines,
  teletekstSkipBottomLines,
  iframeUrl,
  onClose,
  onSave,
  onDelete,
  onImageUpload,
  onPositionChange,
  onLayoutChange,
  onContentChange,
  onSlideNameChange,
  onDurationChange,
  onShowBarChange,
  onVideoUrlChange,
  onVideoSoundChange,
  onImageSideChange,
  onTransitionChange,
  onTeletekstChannelChange,
  onTeletekstThemeChange,
  onTeletekstPagesChange,
  onTeletekstSkipTopLinesChange,
  onTeletekstSkipBottomLinesChange,
  onIframeUrlChange,
  onToggleSlideVisibility,
  onOpenLibrary,
  timeRestriction,
  onTimeRestrictionChange,
  galleryImages,
  onGalleryImageAdd,
  onGalleryImageRemove,
  onGalleryImageDurationChange,
  uploadingGalleryImage,
  onOpenGalleryLibrary,
  onGalleryReorder,
  countdownTitle,
  onCountdownTitleChange,
  countdownTargetDate,
  onCountdownTargetDateChange,
  countdownFormat,
  onCountdownFormatChange,
  countdownBgImage,
  onCountdownBgImageUpload,
  countdownBgImagePosition,
  onCountdownBgImagePositionChange,
  countdownTextColor,
  onCountdownTextColorChange,
  countdownNumberColor,
  onCountdownNumberColorChange,
  countdownBlockBg,
  onCountdownBlockBgChange,
  countdownLabelColor,
  onCountdownLabelColorChange,
  onOpenCountdownLibrary,
  agendaCalendars,
  onAgendaCalendarsChange,
  agendaTitle,
  onAgendaTitleChange,
  agendaDaysAhead,
  onAgendaDaysAheadChange,
  agendaMaxEvents,
  onAgendaMaxEventsChange,
  agendaBgColor,
  onAgendaBgColorChange,
  agendaTextColor,
  onAgendaTextColorChange,
  emailProvider,
  onEmailProviderChange,
  emailCredentials,
  onEmailCredentialsChange,
  emailMaxItems,
  onEmailMaxItemsChange,
  emailShowUnreadOnly,
  onEmailShowUnreadOnlyChange,
  emailBgColor,
  onEmailBgColorChange,
  emailTextColor,
  onEmailTextColorChange,
  emailAccentColor,
  onEmailAccentColorChange,
  sportlinkApiKey,
  sportlinkDataType,
  sportlinkTeams,
  onSportlinkTeamsChange,
  sportlinkTitle,
  onSportlinkTitleChange,
  sportlinkMaxItems,
  onSportlinkMaxItemsChange,
  sportlinkBgColor,
  onSportlinkBgColorChange,
  sportlinkTextColor,
  onSportlinkTextColorChange,
  sportlinkAccentColor,
  onSportlinkAccentColorChange,
  sportlinkHeaderTextColor,
  onSportlinkHeaderTextColorChange,
  sportlinkDate,
  onSportlinkDateChange,
  sportlinkShowVeldInfo,
  onSportlinkShowVeldInfoChange,
  sportlinkOnlyThuis,
  onSportlinkOnlyThuisChange,
  weatherLat,
  onWeatherLatChange,
  weatherLong,
  onWeatherLongChange,
  weatherCity,
  onWeatherCityChange,
  weatherAccentColor,
  onWeatherAccentColorChange,
  weatherLeftAccentColor,
  onWeatherLeftAccentColorChange,
  weatherLeftTextColor,
  onWeatherLeftTextColorChange,
  weatherForecastDays,
  onWeatherForecastDaysChange,
  weatherLeftBgImage,
  onWeatherLeftBgImageUpload,
  weatherLeftBgImagePosition,
  onWeatherLeftBgImagePositionChange,
  onOpenWeatherLeftLibrary,
  qrUrl,
  onQrUrlChange,
  qrLabel,
  onQrLabelChange,
  qrLeftBgColor,
  onQrLeftBgColorChange,
  qrDotsColor,
  onQrDotsColorChange,
  qrPanelColor,
  onQrPanelColorChange,
  qrPanelTextColor,
  onQrPanelTextColorChange,
  qrTextSlides,
  onQrTextSlidesChange,
  qrTextInterval,
  onQrTextIntervalChange,
  onQrSlideImageUpload,
  onOpenQrSlideLibrary,
  uploadingQrSlideId,
  modules = {},
  onSaveSlideEffects,
}) {
  const [timePopupOpen, setTimePopupOpen] = useState(false);
  const [effectsOpen, setEffectsOpen] = useState(false);

  const timeWindowEnabled =
    timeRestriction?.timeEnabled !== undefined
      ? timeRestriction.timeEnabled
      : !!timeRestriction?.enabled;
  const dateWindowEnabled =
    timeRestriction?.dateEnabled !== undefined
      ? timeRestriction.dateEnabled
      : !!timeRestriction?.enabled;

  // Layouts with a plain background that follows the global default
  const supportsSlideBg = ["side-by-side", "text-only", "image-only"].includes(
    slideLayout,
  );
  const effectiveBgColor = slideBgColor || defaultSlideBgColor;

  const renderLayoutContent = () => {
    switch (slideLayout) {
      case "side-by-side":
        return (
          <>
            <div
              className={`slide-modal__body-left ${imageSide === "right" ? "flipped" : ""}`}
            >
              <ImageUpload
                imageUrl={modalImageUrl}
                uploadingImage={uploadingImage}
                onImageUpload={onImageUpload}
                onRemoveImage={() => onImageUpload(null)}
                showPositionSelector={true}
                imagePosition={imagePosition}
                onPositionChange={onPositionChange}
                onOpenLibrary={onOpenLibrary}
              />
            </div>

            <button
              className="slide-modal__flip-btn"
              onClick={() =>
                onImageSideChange(imageSide === "left" ? "right" : "left")
              }
              title="Flip image and text positions"
            >
              <ArrowLeftRight size={20} />
            </button>

            <div
              className={`slide-modal__body-right ${imageSide === "right" ? "flipped" : ""}`}
              style={{ backgroundColor: effectiveBgColor }}
            >
              <div className="text-input-section">
                <TextEditor
                  content={modalTinyMCEContent}
                  onContentChange={onContentChange}
                  enabledFonts={enabledFonts}
                  typography={typography}
                  backgroundColor={effectiveBgColor}
                />
              </div>
            </div>
          </>
        );

      case "image-only":
        return (
          <div
            className="slide-modal__full-image"
            style={{ backgroundColor: effectiveBgColor }}
          >
            <ImageUpload
              imageUrl={modalImageUrl}
              uploadingImage={uploadingImage}
              onImageUpload={onImageUpload}
              onRemoveImage={() => onImageUpload(null)}
              showPositionSelector={true}
              imagePosition={imagePosition}
              onPositionChange={onPositionChange}
              fullWidth={true}
              onOpenLibrary={onOpenLibrary}
            />
          </div>
        );

      case "text-over-image":
        return (
          <div className="slide-modal__text-over-image">
            <div className="slide-modal__image-background">
              <ImageUpload
                imageUrl={modalImageUrl}
                uploadingImage={uploadingImage}
                onImageUpload={onImageUpload}
                onRemoveImage={() => onImageUpload(null)}
                showPositionSelector={true}
                imagePosition={imagePosition}
                onPositionChange={onPositionChange}
                asBackground={true}
                onOpenLibrary={onOpenLibrary}
              />
            </div>

            <div className="slide-modal__text-overlay">
              <div className="text-input-section">
                <TextEditor
                  content={modalTinyMCEContent}
                  onContentChange={onContentChange}
                  enabledFonts={enabledFonts}
                  typography={typography}
                />
              </div>
            </div>
          </div>
        );

      case "text-only":
        return (
          <div
            className="slide-modal__text-only"
            style={{ backgroundColor: effectiveBgColor }}
          >
            <div className="text-input-section-full">
              <TextEditor
                content={modalTinyMCEContent}
                onContentChange={onContentChange}
                enabledFonts={enabledFonts}
                typography={typography}
                backgroundColor={effectiveBgColor}
              />
            </div>
          </div>
        );

      case "video":
        return (
          <div className="modal-video">
            <div className="video-sound-section">
              <button
                type="button"
                className={`video-sound-toggle${videoSound ? " active" : ""}`}
                onClick={() => onVideoSoundChange(!videoSound)}
                title={
                  videoSound
                    ? "Video speelt af met geluid (achtergrondmuziek fadet automatisch weg)"
                    : "Video speelt gedempt af"
                }
              >
                {videoSound ? <Volume2 size={16} /> : <VolumeX size={16} />}
                <span>{videoSound ? "Geluid aan" : "Geluid uit"}</span>
              </button>
            </div>
            <div className="video-input-section">
              <VideoUrlInput
                videoUrl={videoUrl}
                onVideoUrlChange={onVideoUrlChange}
                onRemoveVideo={() => onVideoUrlChange("")}
                onDurationChange={onDurationChange}
              />
            </div>
          </div>
        );

      case "teletekst":
        return (
          <div className="modal-teletekst">
            <div className="teletekst-input-section">
              <TeletekstInput
                channel={teletekstChannel}
                theme={teletekstTheme}
                pages={teletekstPages}
                skipTopLines={teletekstSkipTopLines}
                skipBottomLines={teletekstSkipBottomLines}
                onChannelChange={onTeletekstChannelChange}
                onThemeChange={onTeletekstThemeChange}
                onPagesChange={onTeletekstPagesChange}
                onSkipTopLinesChange={onTeletekstSkipTopLinesChange}
                onSkipBottomLinesChange={onTeletekstSkipBottomLinesChange}
              />
            </div>
          </div>
        );

      case "iframe":
        return (
          <div className="modal-iframe">
            <div className="iframe-input-section">
              <IframeUrlInput
                iframeUrl={iframeUrl}
                onIframeUrlChange={onIframeUrlChange}
                onRemoveIframe={() => onIframeUrlChange("")}
              />
            </div>
          </div>
        );

      case "gallery":
        return (
          <div className="modal-gallery">
            <GalleryInput
              images={galleryImages || []}
              onAddImage={onGalleryImageAdd}
              onRemoveImage={onGalleryImageRemove}
              onDurationChange={onGalleryImageDurationChange}
              uploading={uploadingGalleryImage}
              onOpenLibrary={onOpenGalleryLibrary}
              onReorder={onGalleryReorder}
            />
          </div>
        );

      case "countdown":
        return (
          <div className="modal-countdown">
            <CountdownInput
              countdownTitle={countdownTitle}
              onCountdownTitleChange={onCountdownTitleChange}
              countdownTargetDate={countdownTargetDate}
              onCountdownTargetDateChange={onCountdownTargetDateChange}
              countdownFormat={countdownFormat}
              onCountdownFormatChange={onCountdownFormatChange}
              countdownBgImage={countdownBgImage}
              onCountdownBgImageUpload={onCountdownBgImageUpload}
              countdownBgImagePosition={countdownBgImagePosition}
              onCountdownBgImagePositionChange={
                onCountdownBgImagePositionChange
              }
              countdownTextColor={countdownTextColor}
              onCountdownTextColorChange={onCountdownTextColorChange}
              countdownNumberColor={countdownNumberColor}
              onCountdownNumberColorChange={onCountdownNumberColorChange}
              countdownBlockBg={countdownBlockBg}
              onCountdownBlockBgChange={onCountdownBlockBgChange}
              countdownLabelColor={countdownLabelColor}
              onCountdownLabelColorChange={onCountdownLabelColorChange}
              uploadingImage={uploadingImage}
              onOpenLibrary={onOpenCountdownLibrary}
            />
          </div>
        );

      case "agenda":
        return (
          <div className="modal-agenda">
            <AgendaInput
              agendaCalendars={agendaCalendars}
              onCalendarsChange={onAgendaCalendarsChange}
              agendaTitle={agendaTitle}
              onAgendaTitleChange={onAgendaTitleChange}
              agendaDaysAhead={agendaDaysAhead}
              onAgendaDaysAheadChange={onAgendaDaysAheadChange}
              agendaMaxEvents={agendaMaxEvents}
              onAgendaMaxEventsChange={onAgendaMaxEventsChange}
              agendaBgColor={agendaBgColor}
              defaultBgColor={defaultSlideBgColor}
              onAgendaBgColorChange={onAgendaBgColorChange}
              agendaTextColor={agendaTextColor}
              onAgendaTextColorChange={onAgendaTextColorChange}
            />
          </div>
        );

      case "email":
        return (
          <div className="modal-email">
            <EmailInput
              emailCredentials={emailCredentials}
              onEmailCredentialsChange={onEmailCredentialsChange}
              emailMaxItems={emailMaxItems}
              onEmailMaxItemsChange={onEmailMaxItemsChange}
              emailShowUnreadOnly={emailShowUnreadOnly}
              onEmailShowUnreadOnlyChange={onEmailShowUnreadOnlyChange}
              emailBgColor={emailBgColor}
              defaultBgColor={defaultSlideBgColor}
              onEmailBgColorChange={onEmailBgColorChange}
              emailTextColor={emailTextColor}
              onEmailTextColorChange={onEmailTextColorChange}
              emailAccentColor={emailAccentColor}
              onEmailAccentColorChange={onEmailAccentColorChange}
            />
          </div>
        );

      case "sportlink":
      case "sportlink-programma":
      case "sportlink-uitslagen":
      case "sportlink-poulestand":
        return (
          <div className="modal-sportlink">
            <SportlinkInput
              sportlinkApiKey={sportlinkApiKey}
              sportlinkDataType={sportlinkDataType}
              sportlinkTeams={sportlinkTeams}
              onTeamsChange={onSportlinkTeamsChange}
              sportlinkTitle={sportlinkTitle}
              onTitleChange={onSportlinkTitleChange}
              sportlinkMaxItems={sportlinkMaxItems}
              onMaxItemsChange={onSportlinkMaxItemsChange}
              sportlinkBgColor={sportlinkBgColor}
              defaultBgColor={defaultSlideBgColor}
              onBgColorChange={onSportlinkBgColorChange}
              sportlinkTextColor={sportlinkTextColor}
              onTextColorChange={onSportlinkTextColorChange}
              sportlinkAccentColor={sportlinkAccentColor}
              onAccentColorChange={onSportlinkAccentColorChange}
              sportlinkHeaderTextColor={sportlinkHeaderTextColor}
              onHeaderTextColorChange={onSportlinkHeaderTextColorChange}
              sportlinkDate={sportlinkDate}
              onDateChange={onSportlinkDateChange}
              sportlinkShowVeldInfo={sportlinkShowVeldInfo}
              onShowVeldInfoChange={onSportlinkShowVeldInfoChange}
              sportlinkOnlyThuis={sportlinkOnlyThuis}
              onOnlyThuisChange={onSportlinkOnlyThuisChange}
            />
          </div>
        );

      case "qr-feed":
        return (
          <div className="modal-qr-feed">
            <QrFeedInput
              qrUrl={qrUrl}
              onQrUrlChange={onQrUrlChange}
              qrLabel={qrLabel}
              onQrLabelChange={onQrLabelChange}
              qrLeftBgColor={qrLeftBgColor}
              defaultBgColor={defaultSlideBgColor}
              onQrLeftBgColorChange={onQrLeftBgColorChange}
              qrDotsColor={qrDotsColor}
              onQrDotsColorChange={onQrDotsColorChange}
              qrPanelColor={qrPanelColor}
              onQrPanelColorChange={onQrPanelColorChange}
              qrPanelTextColor={qrPanelTextColor}
              onQrPanelTextColorChange={onQrPanelTextColorChange}
              qrTextSlides={qrTextSlides}
              onQrTextSlidesChange={onQrTextSlidesChange}
              qrTextInterval={qrTextInterval}
              onQrTextIntervalChange={onQrTextIntervalChange}
              onQrSlideImageUpload={onQrSlideImageUpload}
              onOpenQrSlideLibrary={onOpenQrSlideLibrary}
              uploadingQrSlideId={uploadingQrSlideId}
            />
          </div>
        );

      case "weather":
        return (
          <div className="modal-weather">
            <WeatherInput
              weatherLat={weatherLat}
              weatherLong={weatherLong}
              weatherCity={weatherCity}
              weatherAccentColor={weatherAccentColor}
              weatherLeftAccentColor={weatherLeftAccentColor}
              weatherLeftTextColor={weatherLeftTextColor}
              onLatChange={onWeatherLatChange}
              onLongChange={onWeatherLongChange}
              onCityChange={onWeatherCityChange}
              onAccentColorChange={onWeatherAccentColorChange}
              onLeftAccentColorChange={onWeatherLeftAccentColorChange}
              onLeftTextColorChange={onWeatherLeftTextColorChange}
              weatherForecastDays={weatherForecastDays}
              onForecastDaysChange={onWeatherForecastDaysChange}
              weatherLeftBgImage={weatherLeftBgImage}
              weatherLeftBgImagePosition={weatherLeftBgImagePosition}
              onWeatherLeftBgImageUpload={onWeatherLeftBgImageUpload}
              onWeatherLeftBgImagePositionChange={
                onWeatherLeftBgImagePositionChange
              }
              uploadingImage={uploadingImage}
              onOpenWeatherLeftLibrary={onOpenWeatherLeftLibrary}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="slide-modal">
      <div className="slide-modal__overlay" onClick={onClose}>
        <div
          className="slide-modal__content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="slide-modal__header">
            <div className="slide-modal__header-left">
              <div className="slide-modal__name-input-container">
                <input
                  type="text"
                  className="slide-modal__name-input"
                  value={slideName || ""}
                  onChange={(e) => onSlideNameChange(e.target.value)}
                  placeholder="Enter slide name..."
                  maxLength={50}
                />
              </div>
              <div className="slide-modal__duration-container">
                <label htmlFor="slide-duration">
                  <Clock size={16} />
                </label>
                <div className="slide-modal__duration-wrapper">
                  <input
                    id="slide-duration"
                    type="number"
                    className="slide-modal__duration-input"
                    value={slideDuration || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        onDurationChange("");
                      } else {
                        const numValue = parseInt(value);
                        onDurationChange(isNaN(numValue) ? 5 : numValue);
                      }
                    }}
                    min="0"
                    step="1"
                    disabled={slideLayout === "gallery"}
                    title={
                      slideLayout === "gallery"
                        ? "Automatisch berekend op basis van foto-duraties"
                        : undefined
                    }
                  />
                  <span className="slide-modal__duration-suffix">s</span>
                </div>
              </div>
              <div className="slide-modal__showbar-container">
                <div
                  className={`slide-modal__showbar-slider ${showBar ? "active" : ""}`}
                  onClick={() => onShowBarChange(!showBar)}
                  title={showBar ? "Fullscreen mode" : "Two-bar layout mode"}
                >
                  <div className="slider-track">
                    <div className="slider-thumb"></div>
                  </div>
                  <div className="slider-icons">
                    <Maximize2 size={16} className="icon-fullscreen" />
                    <Layout size={16} className="icon-layout" />
                  </div>
                </div>
              </div>
              <div className="slide-modal__transition-container">
                <label htmlFor="slide-transition">
                  <Zap size={16} />
                </label>
                <select
                  id="slide-transition"
                  className="slide-modal__transition-select"
                  value={slideTransition || "fade"}
                  onChange={(e) => onTransitionChange(e.target.value)}
                  title="Select slide transition effect"
                >
                  <option value="fade">Fade</option>
                  <option value="slide-left">Slide Left</option>
                  <option value="slide-right">Slide Right</option>
                  <option value="slide-up">Slide Up</option>
                  <option value="slide-down">Slide Down</option>
                  <option value="zoom-in">Zoom In</option>
                  <option value="zoom-out">Zoom Out</option>
                  <option value="flip-horizontal">Flip Horizontal</option>
                  <option value="flip-vertical">Flip Vertical</option>
                  <option value="none">None</option>
                </select>
              </div>
              {supportsSlideBg && (
                <div
                  className="slide-modal__bg-container"
                  title="Achtergrondkleur van deze slide"
                >
                  <BackgroundColorInput
                    value={slideBgColor}
                    defaultColor={defaultSlideBgColor}
                    onChange={onSlideBgColorChange}
                  />
                </div>
              )}
            </div>

            <div className="slide-modal__header-center">
              <LayoutSelector
                currentLayout={slideLayout}
                onLayoutChange={onLayoutChange}
                slideTypes={slideTypes}
              />
            </div>

            <div className="slide-modal__header-actions">
              {(() => {
                const timeRestrictionActive =
                  timeWindowEnabled || dateWindowEnabled;
                const expired = isCountdownExpired(slide);
                const effectivelyVisible = expired
                  ? false
                  : timeRestrictionActive
                    ? isSlideCurrentlyInWindow(slide)
                    : slide.isVisible;
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSlideVisibility(slide.id);
                    }}
                    disabled={timeRestrictionActive}
                    className={`btn-icon btn-icon--time ${effectivelyVisible ? "btn-icon--success" : ""}`}
                    title={
                      timeRestrictionActive
                        ? `Tijdvenster actief — nu ${effectivelyVisible ? "zichtbaar" : "verborgen"}`
                        : "Toggle slide"
                    }
                  >
                    {effectivelyVisible ? (
                      <Eye size={16} />
                    ) : (
                      <EyeOff size={16} />
                    )}
                  </button>
                );
              })()}

              <div className="slide-modal__time-popup-wrapper">
                <button
                  className={`btn-icon btn-icon--time ${timeWindowEnabled || dateWindowEnabled ? "btn-icon--success" : ""}`}
                  title="Tijdvenster instellen"
                  onClick={() => setTimePopupOpen(!timePopupOpen)}
                >
                  <Clock size={16} />
                </button>
                {timePopupOpen && (
                  <div className="slide-modal__time-popup">
                    <div className="time-popup__header">
                      <span>Tijdvenster</span>
                      <button
                        className="time-popup__close"
                        onClick={() => setTimePopupOpen(false)}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="time-popup__date-section">
                      <label className="time-popup__toggle">
                        <input
                          type="checkbox"
                          checked={timeWindowEnabled}
                          onChange={(e) =>
                            onTimeRestrictionChange({
                              ...timeRestriction,
                              timeEnabled: e.target.checked,
                            })
                          }
                        />
                        <span>Tijdvenster inschakelen</span>
                      </label>
                      {timeWindowEnabled && (
                        <div className="time-popup__inputs">
                          <div className="time-popup__row">
                            <div className="time-popup__field">
                              <label>Van</label>
                              <input
                                type="time"
                                value={timeRestriction.startTime || "08:00"}
                                onChange={(e) =>
                                  onTimeRestrictionChange({
                                    ...timeRestriction,
                                    startTime: e.target.value,
                                  })
                                }
                                className="time-popup__time-input"
                              />
                            </div>
                            <div className="time-popup__field">
                              <label>Tot</label>
                              <input
                                type="time"
                                value={timeRestriction.endTime || "17:00"}
                                onChange={(e) =>
                                  onTimeRestrictionChange({
                                    ...timeRestriction,
                                    endTime: e.target.value,
                                  })
                                }
                                className="time-popup__time-input"
                              />
                            </div>
                          </div>
                          {timeRestriction.startTime >
                            timeRestriction.endTime && (
                            <p className="time-popup__midnight-note">
                              ↻ Loopt over middernacht
                            </p>
                          )}

                          <div className="time-popup__days">
                            {[
                              { key: "mon", label: "Ma" },
                              { key: "tue", label: "Di" },
                              { key: "wed", label: "Wo" },
                              { key: "thu", label: "Do" },
                              { key: "fri", label: "Vr" },
                              { key: "sat", label: "Za" },
                              { key: "sun", label: "Zo" },
                            ].map(({ key, label }) => (
                              <label
                                key={key}
                                className="time-popup__day-label"
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    timeRestriction.days?.[key] !== false
                                  }
                                  onChange={(e) =>
                                    onTimeRestrictionChange({
                                      ...timeRestriction,
                                      days: {
                                        mon: true,
                                        tue: true,
                                        wed: true,
                                        thu: true,
                                        fri: true,
                                        sat: true,
                                        sun: true,
                                        ...timeRestriction.days,
                                        [key]: e.target.checked,
                                      },
                                    })
                                  }
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="time-popup__date-section">
                      <label className="time-popup__toggle">
                        <input
                          type="checkbox"
                          checked={dateWindowEnabled}
                          onChange={(e) =>
                            onTimeRestrictionChange({
                              ...timeRestriction,
                              dateEnabled: e.target.checked,
                            })
                          }
                        />
                        <span>Datumvenster inschakelen</span>
                      </label>
                      {dateWindowEnabled && (
                        <div className="time-popup__row">
                          <div className="time-popup__field">
                            <label>Vanaf</label>
                            <input
                              type="date"
                              value={timeRestriction.startDate || ""}
                              onChange={(e) =>
                                onTimeRestrictionChange({
                                  ...timeRestriction,
                                  startDate: e.target.value,
                                })
                              }
                              className="time-popup__time-input"
                            />
                          </div>
                          <div className="time-popup__field">
                            <label>Tot en met</label>
                            <input
                              type="date"
                              value={timeRestriction.endDate || ""}
                              onChange={(e) =>
                                onTimeRestrictionChange({
                                  ...timeRestriction,
                                  endDate: e.target.value,
                                })
                              }
                              className="time-popup__time-input"
                            />
                          </div>
                          {timeRestriction.startDate &&
                            timeRestriction.endDate &&
                            timeRestriction.startDate >
                              timeRestriction.endDate && (
                              <p className="time-popup__midnight-note">
                                ⚠ Einddatum ligt voor de startdatum
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {modules.slideEffects && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEffectsOpen(true);
                  }}
                  className={`btn-icon ${slide.effects?.length > 0 ? "btn-icon--effects-active" : ""}`}
                  title="Slide effecten"
                >
                  <Sparkles size={16} />
                </button>
              )}

              <button
                onClick={() => {
                  console.log("Delete button clicked");
                  onDelete();
                }}
                className="btn-icon btn-icon--danger"
                title="Delete slide"
              >
                <Trash2 size={16} />
              </button>
              <button onClick={onClose} className="btn btn-secondary">
                Close
              </button>
              <button onClick={onSave} className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </div>

          <div className="slide-modal__body">{renderLayoutContent()}</div>

          <div className="slide-modal__mobile-footer">
            <button
              onClick={() => {
                onDelete();
              }}
              className="btn-icon btn-icon--danger slide-modal__mobile-footer-delete"
              title="Slide verwijderen"
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={onSave}
              className="btn btn-primary slide-modal__mobile-footer-save"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>

      {effectsOpen && (
        <SlideEffectsModal
          slide={slide}
          onClose={() => setEffectsOpen(false)}
          onSave={(effects) => {
            if (onSaveSlideEffects) onSaveSlideEffects(effects);
            setEffectsOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default EditModal;
